// Client-side face masking for the Quick Live Review photo step.
//
// GOAL: the un-masked original image is NEVER uploaded. We detect faces on the
// captured still, pixelate (mosaic) the detected regions onto an offscreen
// canvas, and export the canvas as a JPEG File. Only that masked File is sent.
//
// APPROACH: post-capture (still-image) masking rather than a real-time preview.
// Real-time canvas masking on the camera feed is heavier and less reliable
// across the range of mobile browsers a QR-landing page has to support, and
// this flow already captures a still via the Camera plugin / file input. So we
// run detection once on the captured frame — simpler and reliable.
//
// MODEL LOADING: MediaPipe's WASM runtime + the face-detector model are loaded
// lazily. The WASM binaries are resolved from the jsDelivr CDN (the standard
// approach documented by MediaPipe) and the short-range BlazeFace model is
// pulled from Google's model storage. This whole module is dynamically
// imported only on the live-review route, so the WASM/model never bloat the
// main bundle.

import { FaceDetector, FilesetResolver, type Detection } from '@mediapipe/tasks-vision';

// Pin the tasks-vision version so the WASM assets match the installed package.
const WASM_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';
const FACE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';

let detectorPromise: Promise<FaceDetector> | null = null;

// Lazily create (and cache) the FaceDetector. IMAGE running mode since we
// detect on a single captured still, not a live stream.
const getDetector = async (): Promise<FaceDetector> => {
  if (!detectorPromise) {
    detectorPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_CDN);
      return FaceDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: FACE_MODEL_URL },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.5,
      });
    })();
  }
  return detectorPromise;
};

export interface MaskResult {
  // Object URL of the masked image, for preview. Caller must revoke it.
  previewUrl: string;
  // The masked image as a File, ready to upload as the `photo` field.
  file: File;
  // How many faces were detected & masked. 0 means detection found nothing.
  facesMasked: number;
  // N5: distinct modes so the UI can show "auto-masked" vs "manual full blur".
  // 'auto'   — MediaPipe detected and masked N faces
  // 'manual' — user explicitly chose "blur the whole photo" (forceFullBlur)
  // 'noface' — no faces detected AND user didn't request manual blur
  mode: 'auto' | 'manual' | 'noface';
}

// Load an <img> from a source URL. crossOrigin='anonymous' is required so a
// remote (R2) image can be drawn to a canvas without tainting it — the caller
// handles the tainted-canvas fallback.
const loadImage = (src: string, crossOrigin?: boolean): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image-load-failed'));
    img.src = src;
  });

// Pixelate (mosaic) a rectangular region of the canvas in place. A mosaic is
// stronger and cheaper than a gaussian blur and cannot be reversed.
const pixelateRegion = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  // ~12 blocks across the face region, clamped so tiny faces still get chunky
  // blocks and huge faces don't get slow.
  const block = Math.max(8, Math.round(Math.max(w, h) / 12));
  for (let py = y; py < y + h; py += block) {
    for (let px = x; px < x + w; px += block) {
      const bw = Math.min(block, x + w - px);
      const bh = Math.min(block, y + h - py);
      // Sample the top-left pixel of the block and paint the whole block with it.
      const data = ctx.getImageData(px, py, 1, 1).data;
      ctx.fillStyle = `rgba(${data[0]},${data[1]},${data[2]},1)`;
      ctx.fillRect(px, py, bw, bh);
    }
  }
};

// Expand a detection box so it comfortably covers hair/jaw beyond the tight
// face box, and clamp to the image bounds.
const expandBox = (
  box: { originX: number; originY: number; width: number; height: number },
  imgW: number,
  imgH: number,
) => {
  const padX = box.width * 0.25;
  const padY = box.height * 0.35;
  const x = Math.max(0, Math.floor(box.originX - padX));
  const y = Math.max(0, Math.floor(box.originY - padY));
  const w = Math.min(imgW - x, Math.ceil(box.width + padX * 2));
  const h = Math.min(imgH - y, Math.ceil(box.height + padY * 2));
  return { x, y, w, h };
};

export const canvasToFile = (canvas: HTMLCanvasElement): Promise<File> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('canvas-export-failed'));
          return;
        }
        resolve(new File([blob], `live-review-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  });

// Core: draw the image to a canvas, optionally mask detected faces, export.
const renderMasked = async (
  img: HTMLImageElement,
  detections: Detection[],
  forceFullBlur: boolean,
): Promise<MaskResult> => {
  // Cap the longest edge so uploads stay reasonable and detection coords map
  // cleanly onto the drawn canvas.
  const maxEdge = 1280;
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-context-failed');
  ctx.drawImage(img, 0, 0, w, h);

  let facesMasked = 0;

  let mode: 'auto' | 'manual' | 'noface' = 'noface';
  if (forceFullBlur) {
    // Manual "blur my whole photo" fallback: pixelate the upper-center region
    // where a face most likely is when detection failed but the user still
    // wants privacy. Covers the top 70% of the frame.
    pixelateRegion(ctx, 0, 0, w, Math.round(h * 0.7));
    facesMasked = 1;
    mode = 'manual';
  } else if (detections.length === 0) {
    mode = 'noface';
  } else {
    for (const det of detections) {
      const bbox = det.boundingBox;
      if (!bbox) continue;
      // Detection coords are in the original image space; scale to canvas.
      const region = expandBox(
        {
          originX: bbox.originX * scale,
          originY: bbox.originY * scale,
          width: bbox.width * scale,
          height: bbox.height * scale,
        },
        w,
        h,
      );
      pixelateRegion(ctx, region.x, region.y, region.w, region.h);
      facesMasked += 1;
    }
    mode = 'auto';
  }

  const file = await canvasToFile(canvas);
  return { previewUrl: URL.createObjectURL(file), file, facesMasked, mode };
};

// Mask a locally captured photo (from the customer's own camera/gallery). This
// path must ALWAYS work — the image is same-origin (blob/data URL) so the
// canvas never taints.
export const maskLocalPhoto = async (src: string, forceFullBlur = false): Promise<MaskResult> => {
  const img = await loadImage(src);
  let detections: Detection[] = [];
  if (!forceFullBlur) {
    try {
      const detector = await getDetector();
      detections = detector.detect(img).detections ?? [];
    } catch (err) {
      // Detection failed to load/run — degrade gracefully. We return the image
      // unmasked ONLY if the caller didn't force a blur; the UI warns the user
      // no face was found and offers the manual blur toggle / retake.
      console.warn('Face detection unavailable, returning unmasked:', err);
    }
  }
  return renderMasked(img, detections, forceFullBlur);
};

export type PendingMaskResult = MaskResult | { tainted: true };

// Mask a stylist-pre-attached photo hosted on R2 (a different origin).
// crossOrigin is needed to read pixels; if the canvas still taints (bucket
// CORS not configured) we detect it and signal the caller to fall back to the
// customer taking their own photo.
//
// NOTE: reading a cross-origin R2 image into a canvas requires the R2 bucket to
// serve permissive CORS headers (Access-Control-Allow-Origin) on the signed
// URL. If that's not configured the canvas becomes "tainted" and toBlob throws
// a SecurityError — we catch that and return { tainted: true }.
export const maskPendingPhoto = async (
  url: string,
  forceFullBlur = false,
): Promise<PendingMaskResult> => {
  try {
    const img = await loadImage(url, true);
    let detections: Detection[] = [];
    if (!forceFullBlur) {
      try {
        const detector = await getDetector();
        detections = detector.detect(img).detections ?? [];
      } catch (err) {
        console.warn('Face detection unavailable for pending photo:', err);
      }
    }
    return await renderMasked(img, detections, forceFullBlur);
  } catch (err) {
    // A SecurityError from toBlob (tainted canvas) or an image-load CORS error
    // lands here. Signal the caller to fall back to customer-takes-own-photo.
    console.warn('Pending photo could not be masked (likely CORS/taint):', err);
    return { tainted: true };
  }
};

// ---------------------------------------------------------------------------
// Manual finger-blur (2026-08-31)
//
// The stylist paints strokes over the photo in the ManualBlurEditor; each
// stroke is a list of points normalized to the image's width/height (0–1).
// applyManualBlur rasterizes the strokes at export resolution and mosaic-
// pixelates the painted blocks, using the same visual language (and max-edge
// cap) as the auto mask above — so the customer sees one consistent "hidden"
// treatment no matter which path masked the photo.
// ---------------------------------------------------------------------------

/** One paint stroke. Points are normalized (0–1) against the image's w/h. */
export interface ManualStroke {
  points: { x: number; y: number }[];
}

/**
 * Apply finger-painted blur to a photo.
 *
 * @param src            image source (blob/object URL of the local file)
 * @param strokes        painted strokes in normalized coords
 * @param brushRadiusNorm brush radius as a fraction of min(naturalW, naturalH)
 */
export const applyManualBlur = async (
  src: string,
  strokes: ManualStroke[],
  brushRadiusNorm: number,
): Promise<MaskResult> => {
  if (strokes.length === 0) throw new Error('no-strokes');
  const img = await loadImage(src);

  // Same export-size rule as renderMasked: cap the longest edge, no upscale.
  const maxEdge = 1280;
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-context-failed');
  ctx.drawImage(img, 0, 0, w, h);

  // Rasterize the strokes onto an offscreen mask at export resolution.
  const mask = document.createElement('canvas');
  mask.width = w;
  mask.height = h;
  const mctx = mask.getContext('2d');
  if (!mctx) throw new Error('canvas-context-failed');
  mctx.fillStyle = '#000';
  const radius = Math.max(6, brushRadiusNorm * Math.min(w, h));
  for (const stroke of strokes) {
    for (const p of stroke.points) {
      mctx.beginPath();
      mctx.arc(p.x * w, p.y * h, radius, 0, Math.PI * 2);
      mctx.fill();
    }
  }

  // Grid the image; pixelate every block whose mask-centre is painted. Read
  // both canvases ONCE into buffers — per-block getImageData calls cost ~1000
  // readbacks and froze low-end phones for seconds on "Done".
  const block = Math.max(14, Math.round(Math.min(w, h) / 24));
  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;
  const maskData = mctx.getImageData(0, 0, w, h).data;

  for (let by = 0; by < h; by += block) {
    for (let bx = 0; bx < w; bx += block) {
      const bw = Math.min(block, w - bx);
      const bh = Math.min(block, h - by);
      // Mask centre: painted blocks are pure black (alpha > 0, r === 0).
      const mcx = bx + (bw >> 1);
      const mcy = by + (bh >> 1);
      const mi = (mcy * w + mcx) * 4;
      if (!(maskData[mi] === 0 && maskData[mi + 3] > 0)) continue;
      // Fill the block with its top-left pixel (same as pixelateRegion).
      const si = (by * w + bx) * 4;
      const r = d[si];
      const g = d[si + 1];
      const b = d[si + 2];
      for (let y = by; y < by + bh; y++) {
        let di = (y * w + bx) * 4;
        for (let x = 0; x < bw; x++) {
          d[di] = r;
          d[di + 1] = g;
          d[di + 2] = b;
          di += 4;
        }
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);

  const file = await canvasToFile(canvas);
  return { previewUrl: URL.createObjectURL(file), file, facesMasked: 0, mode: 'manual' };
};
